import { useState } from "react"
import { Formik, Form } from "formik"
import { AnimatePresence } from "framer-motion"
import * as Yup from "yup"
import { XMarkIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import Button from "~/ui-rtk/components/ui/controls/Button"
import Input from "~/ui-rtk/components/ui/controls/Input"
import { useCustomAttributionControllerCreateInitiativeTypeMutation } from "~/ui-rtk/api/customAttributionApi"
import { InitiativeCategory } from "~/ui-rtk/constants/initiative-types"

interface CreateInitiativeTypeModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface CreateInitiativeTypeFormValues {
  label: string
  category: InitiativeCategory
}

// RTK Query surfaces server errors as { data: { message } } where message may be
// a string or an array of validation strings. Fall back to a generic message.
function getApiErrorMessage(error: unknown): string {
  if (typeof error === "object" && error !== null && "data" in error) {
    const data = (error as { data?: { message?: string | string[] } }).data
    if (data?.message) {
      return Array.isArray(data.message)
        ? data.message.join(", ")
        : data.message
    }
  }
  return "Failed to create the initiative type. Please try again."
}

const validationSchema = Yup.object().shape({
  label: Yup.string().trim().required("Name is required"),
  category: Yup.string().required("Category is required"),
})

const CATEGORY_OPTIONS: Array<{
  value: InitiativeCategory
  label: string
  description: string
}> = [
  {
    value: InitiativeCategory.DEMAND_CAPTURE,
    label: "Demand Capture",
    description: "Optimized against direct-response ROAS",
  },
  {
    value: InitiativeCategory.DEMAND_GENERATION,
    label: "Demand Generation",
    description: "Optimized against Brand ROAS",
  },
]

export function CreateInitiativeTypeModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateInitiativeTypeModalProps) {
  const [createInitiativeType] =
    useCustomAttributionControllerCreateInitiativeTypeMutation()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const initialValues: CreateInitiativeTypeFormValues = {
    label: "",
    category: InitiativeCategory.DEMAND_CAPTURE,
  }

  const handleSubmit = async (values: CreateInitiativeTypeFormValues) => {
    setErrorMessage(null)
    setIsSaving(true)
    try {
      await createInitiativeType({
        label: values.label.trim(),
        category: values.category,
      }).unwrap()
      onSuccess?.()
      onClose()
    } catch (error: unknown) {
      console.error("Failed to create initiative type:", error)
      setErrorMessage(getApiErrorMessage(error))
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) {
    if (errorMessage) {
      setErrorMessage(null)
    }
    return null
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex justify-center pt-[15vh]">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative z-10 bg-system-blue-800 border border-system-blue-600/50 rounded-system-xl shadow-2xl max-h-[75vh] overflow-y-auto w-[480px] h-fit">
          {isSaving && (
            <div className="absolute inset-0 bg-system-blue-800/20 z-50 flex items-center justify-center rounded-system-xl">
              <div className="bg-system-blue-800 border border-system-blue-600/50 rounded-lg px-6 py-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 border-2 border-system-blue-400 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-white font-medium">Creating...</span>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="bg-system-blue-700 border-b border-system-blue-600/50 px-system-md py-system-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">
                New Initiative Type
              </h2>
              <button
                onClick={onClose}
                className="text-system-gray-400 hover:text-white transition-colors p-1 rounded-md hover:bg-system-blue-600/20"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, setFieldValue, handleChange }) => (
              <Form className="flex flex-col gap-system-md p-system-md">
                {/* Name */}
                <div className="flex flex-col gap-system-xs">
                  <span className="text-base font-medium text-white">Name</span>
                  <Input
                    name="label"
                    fullWidth
                    value={values.label}
                    onChange={handleChange}
                    isError={!!(touched.label && errors.label)}
                    placeholder="e.g. Influencer"
                  />
                  {touched.label && errors.label && (
                    <span className="text-sm text-system-error-300">
                      {errors.label}
                    </span>
                  )}
                </div>

                {/* Category */}
                <div className="flex flex-col gap-system-xs">
                  <span className="text-base font-medium text-white">
                    Category
                  </span>
                  <div className="grid grid-cols-2 gap-system-sm">
                    {CATEGORY_OPTIONS.map(option => {
                      const isActive = values.category === option.value
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() =>
                            void setFieldValue("category", option.value)
                          }
                          className={`flex flex-col items-start gap-1 text-left p-system-sm rounded-lg border transition-all ${
                            isActive
                              ? "border-basic-pink bg-basic-pink/10"
                              : "border-system-blue-600/40 hover:border-system-blue-500/60 bg-system-blue-600/5"
                          }`}
                        >
                          <span className="text-base font-medium text-white">
                            {option.label}
                          </span>
                          <span className="text-sm text-system-gray-400">
                            {option.description}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Error Message */}
                {errorMessage && (
                  <div className="flex items-center gap-2 p-system-sm bg-system-error-500/10 border border-system-error-500/30 rounded-lg">
                    <ExclamationTriangleIcon className="w-5 h-5 text-system-error-400 flex-shrink-0" />
                    <span className="text-sm text-system-error-300">
                      {errorMessage}
                    </span>
                  </div>
                )}

                {/* Buttons */}
                <div className="flex justify-start space-x-2 pt-system-xs border-t border-system-blue-600/30">
                  <Button
                    type="button"
                    variant={{ variant: "text" }}
                    onClick={onClose}
                    disabled={isSaving}
                    className="px-system-md py-system-xs text-base"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant={{ variant: "solid", color: "pink" }}
                    disabled={isSaving}
                    className="px-system-md py-system-xs text-base text-white"
                  >
                    {isSaving ? "Creating..." : "Create"}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </AnimatePresence>
  )
}
